import React, { useEffect, useState } from "react";
import { Typography, Grid2, Divider } from "@mui/material";
import { getDataForAllOption, getPageTitle, sortDataAlphabetically } from "../../common/CommonFunctions";
import { getUserRoleAndScreens } from "../../services/authServices/auth.services";
import { setRequiredDataForDropdown } from "../../common/CommonFunctions";
import { getDashboardDataOfAuditStatusCount, getAllBranches, getAllDepartmentList } from "../../helpers/api-functions-helper";
import { useSnackbar } from "../../contexts/Snackbar.context";
import { useBackdrop } from "../../contexts/BackDrop.context";
import { APP_MESSAGE, ERROR_MESSAGE, SNACKBAR_VARIANT } from "../../config/Messages.config";
import { useNavigate } from "react-router-dom";
import { DashboardCardConfig, ThirdPartyAuditConfigData } from "./components/Dashboard.config";
import DashboardCard from "./components/DashboardCard.component";
import MUILoader from "../../components/MUILoader/MUILoader";
import { ROLES } from "../../config/Roles.config";
import AppAutoCompleteField from "../../components/CommonReusableComponent/AppAutoCompleteField/AppAutoCompleteField";
import { Icons } from "../../config/MuiIcons.config";
import { AppTheme } from "../../theme/App.theme";

const CompanyDashboard = () => {
  const documentTitle = getPageTitle() && getPageTitle();
  document.title = documentTitle
    ? `${documentTitle} | Zing Auditor`
    : "Zing Auditor";
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const { accountId, role, userUid } =
    getUserRoleAndScreens();

  // Page Loading state, for dashboard status count
  const [isPageLoading, setIsPageLoading] = useState(false);

  // All Object for selection
  const allObjWithNameId = [{ name: "All", id: "All" }];
  // const allObjWithLabelValue = [{ label: "All", value: "All" }];

  // Branch and department id's and their data states
  // States
  const [branchId, setBranchId] = useState(null);
  const [departmentId, setDepartmentId] = useState(null);
  // data
  const [branches, setBranches] = useState([]);
  const [deptInstance, setDeptInstance] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Audit count data state
  const [dashboardCardLayoutData, setDashboardCardLayoutData] = useState([
    ...DashboardCardConfig,
  ]);

  const [thirdPartyCardLayoutData, setThirdPartyCardLayoutData] = useState([
    ...ThirdPartyAuditConfigData,
  ]);

  // loading states
  const [loadingState, setLoadingState] = useState({
    branches: false,
    departments: false,
  });

  // User types
  // Super-Admin
  const isSuperAdmin = role && role.toLowerCase() === ROLES.SUPERADMIN ? true : false;
  // Admin
  const isAdmin = role && role.toLowerCase() === ROLES.ADMIN ? true : false;
  // User
  const isUser = role && role.toLowerCase() === ROLES.USER ? true : false;

  // Common Style for Box-Shadow
  const cardStyle = {
    padding: '1.2rem',
    borderRadius: "8px",
    boxShadow: "0 0 8px rgba(93, 101, 108, 0.3)",
  }

  // Function to hide Create New Audit button
  const hideCreateAuditButton = (type) => {
    if (type === "Internal") {
      return isUser;
    }

    if (type === "Third-Party") {
      return isSuperAdmin || isAdmin;
    }
  };

  // Global loading state
  const isAllLoadingComplete = Object.values(loadingState).every(
    (status) => !status
  );

  // Function to update loading state
  const setLoading = (key, value) => {
    setLoadingState((prev) => ({ ...prev, [key]: value }));
  };

  // Function to fetch dashboard data for normal user
  const fetchDashboardData = (branchId, departmentId) => {
    setIsPageLoading(true);
    const payload = {
      branchIds: getDataForAllOption("branch", branchId, branches),
      departmentIds: getDataForAllOption("department", departmentId, departments),
      userId: userUid,
      accountId: accountId,
    };
    getDashboardDataOfAuditStatusCount(payload)
      .then((response) => {
        if (response && response.data) {
          const { statusCode, data, message, success } = response.data;
          if (success && statusCode === 200) {
            setDashboardCardLayoutData((prev) =>
              prev.map((item) => {
                return {
                  ...item,
                  count: data.internalAudit[item.statusType] || 0,
                };
              })
            );
            setThirdPartyCardLayoutData((prev) =>
              prev.map((item) => {
                return {
                  ...item,
                  count: data.thirdPartyAudit[item.statusType] || 0,
                };
              })
            );
          } else {
            setDashboardCardLayoutData([...DashboardCardConfig]);
            setThirdPartyCardLayoutData([...ThirdPartyAuditConfigData]);
            showSnackbar(
              message || data || APP_MESSAGE.APP_DASHBOARD.FAILED,
              SNACKBAR_VARIANT.ERROR
            );
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(APP_MESSAGE.APP_DASHBOARD.FAILED, SNACKBAR_VARIANT.ERROR);
      })
      .finally(() => {
        setIsPageLoading(false);
      });
  };

  // Fetch all department by branch id
  const fetchAllDepartmentByAccountId = () => {
    setLoading("departments", true);
    getAllDepartmentList(accountId)
      .then((response) => {
        if (response && response.data) {
          const { statusCode, data } = response.data;
          if (statusCode && statusCode === 200) {
            if (data && data?.length > 0) {
              let dataSet = [...allObjWithNameId, ...data];
              const sortedData = sortDataAlphabetically(dataSet, "name");
              setRequiredDataForDropdown(
                setDepartments,
                sortedData,
                "name",
                "id"
              );
              setDeptInstance(data);
              setDepartmentId("All");
            }
          } else {
            setDepartments([]);
            setDeptInstance([]);
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          ERROR_MESSAGE.FAILED_TO_FETCH_DATA,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        setLoading("departments", false);
      });
  };

  // Function to fetch all branches
  const fetchAllBranches = () => {
    setLoading("branches", true);
    getAllBranches(accountId)
      .then((response) => {
        if (response && response.data) {
          const { statusCode, data } = response.data;
          if (statusCode && statusCode === 200) {
            if (data && data?.length > 0) {
              const activeBranches = [...data].filter(branch => branch.isActive)
              let dataSet = [...allObjWithNameId, ...activeBranches];
              const sortedData = sortDataAlphabetically(dataSet, "name");
              setRequiredDataForDropdown(setBranches, sortedData, "name", "id");
              setBranchId("All");
              fetchAllDepartmentByAccountId();
            }
          } else {
            setBranches([]);
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          ERROR_MESSAGE.FAILED_TO_FETCH_DATA,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        setLoading("branches", false);
      });
  };

  // Function to filter dept by branch
  const getFilterDeptByBranch = (Branch_Id) => {
    if (Branch_Id === "All") {
      setRequiredDataForDropdown(
        setDepartments,
        [...allObjWithNameId, ...deptInstance],
        "name",
        "id"
      );
      setDepartmentId("All")
    } else {
      const filterDept = deptInstance.filter(
        (dept) => dept.branchId === Branch_Id
      );
      setRequiredDataForDropdown(
        setDepartments,
        [...allObjWithNameId, ...filterDept],
        "name",
        "id"
      );
      setDepartmentId("All")
    }
  };

  const setFieldValue = (fieldName, value) => {
    if (fieldName === "branchId") {
      setBranchId(value);
      getFilterDeptByBranch(value);
    } else {
      setDepartmentId(value);
    }
  };

  // To handle change branch & department
  const handleChangeFilter = (Data, FieldName) => {
    if (!Data) {
      // If no value is selected, set null
      setFieldValue(FieldName, null);
      return;
    } else {
      setFieldValue(FieldName, Data.value)
    }
  };

  useEffect(() => {
    fetchAllBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (branchId && departmentId) {
      fetchDashboardData(branchId, departmentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, departmentId]);

  // Handle global loading state
  useEffect(() => {
    if (!isAllLoadingComplete) {
      showBackdrop();
    } else {
      hideBackdrop();
    }
  }, [isAllLoadingComplete]);

  // Function used to navigate user on card click
  const navigateUserOnCardPress = (DATA, AuditType) => {
    if (branchId && departmentId) {
      const { status } = DATA;
      const locationData = {
        auditStatus: status,
        dashboardBranchId: branchId,
        dashboardDepartmentId: departmentId,
      };
      if (AuditType === "Internal") {
        navigate("/audit-master", {
          state: locationData,
        });
      } else {
        navigate("/third-party-audit-master", {
          state: locationData,
        });
      }
    } else {
      showSnackbar(
        "Please select a department to view its related audits.",
        SNACKBAR_VARIANT.ERROR
      );
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Grid2 container className="d-flex align-items-center my-1" spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
            <Typography className="inter-medium-500">Created Audits</Typography>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 3, md: 3, lg: 3 }}>
            <AppAutoCompleteField
              IsMultiple={false}
              Options={branches || []}
              TextInputName={"branchId"}
              TextInputId={"branchId"}
              Id={"branchId"}
              TextInputLabel={"Select branch"}
              TextInputPlaceholder={"Select branch"}
              HandleOnChange={(event, value) => {
                handleChangeFilter(value, "branchId");
              }}
              Value={branchId}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 3, md: 3, lg: 3 }} className="py-1">
            <AppAutoCompleteField
              IsMultiple={false}
              Options={departments || []}
              Disabled={branchId === null || branchId === "All"}
              TextInputName={"departmentId"}
              TextInputId={"departmentId"}
              Id={"departmentId"}
              TextInputLabel={"Select department"}
              TextInputPlaceholder={"Select department"}
              HandleOnChange={(event, value) => {
                handleChangeFilter(value, "departmentId");
              }}
              Value={departmentId}
            />
          </Grid2>
        </Grid2>

        <Divider
          style={{ color: "GrayText", border: "0.02rem solid GrayText" }}
          className="mt-2 mb-3"
        />

        {isPageLoading ? (
          <MUILoader />
        ) : (
          <Grid2 container rowSpacing={3} columnSpacing={3}>
            <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
              <Grid2
                container
                spacing={2}
                display={"flex"}
                alignItems={"center"}
                className="mb-2"
                style={{...cardStyle}} 
              >
                <Grid2 size={{ xs: 6, sm: 6, md: 7, lg: 7, xl: 9 }}>
                  <Typography className="inter-regular-400">
                    Internal Audits
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 6, md: 5, lg: 5, xl: 3 }}>
                  <Typography
                    className="cursor-pointer inter-regular-400 d-flex align-items-center justify-content-end"
                    sx={{
                      visibility: !hideCreateAuditButton("Internal")
                        ? "visible"
                        : "hidden",
                      color: AppTheme.PRIMARY_COLOR,
                    }}
                    onClick={() => {
                      navigate("/audit-master/create");
                    }}
                  >
                    <span>Create New</span>{" "}
                    <Icons.OpenInNewIcon fontSize="small" className="ms-1" />
                  </Typography>
                </Grid2>
                {dashboardCardLayoutData.map((data, idx) => (
                  <Grid2 size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }} key={idx}>
                    <DashboardCard
                      key={idx}
                      CardCount={data.count}
                      CardTitle={data.title}
                      CardBackgroundColor={data.cardBackgroundColor}
                      CountBackgroundColor={data.countBackgroundColor}
                      CardImage={data.cardImage}
                      OnPressNavigate={() =>
                        navigateUserOnCardPress(data, "Internal")
                      }
                    />
                  </Grid2>
                ))}
              </Grid2>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
              <Grid2
                container
                spacing={2}
                display={"flex"}
                alignItems={"center"}
                className="mb-2"
                style={{...cardStyle}}
              >
                <Grid2 size={{ xs: 6, sm: 6, md: 7, lg: 7, xl: 9 }}>
                  <Typography className="inter-regular-400">
                    Third Party Audits
                  </Typography>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 6, md: 5, lg: 5, xl: 3 }}>
                  <Typography
                    className="cursor-pointer inter-regular-400 d-flex align-items-center justify-content-end"
                    sx={{
                      visibility: !hideCreateAuditButton("Third-Party")
                        ? "visible"
                        : "hidden",
                      color: AppTheme.PRIMARY_COLOR,
                    }}
                    onClick={() => {
                      navigate("/third-party-audit-master/create");
                    }}
                  >
                    <span>Create New</span>{" "}
                    <Icons.OpenInNewIcon fontSize="small" className="ms-1" />
                  </Typography>
                </Grid2>
                {thirdPartyCardLayoutData.map((data, idx) => (
                  <Grid2 size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }} key={idx}>
                    <DashboardCard
                      key={idx}
                      CardCount={data.count}
                      CardTitle={data.title}
                      CardBackgroundColor={data.cardBackgroundColor}
                      CountBackgroundColor={data.countBackgroundColor}
                      CardImage={data.cardImage}
                      OnPressNavigate={() =>
                        navigateUserOnCardPress(data, "Third Party")
                      }
                    />
                  </Grid2>
                ))}
              </Grid2>
            </Grid2>
          </Grid2>
        )}
      </div>
    </React.Fragment>
  );
};

export default CompanyDashboard;
